import OnlineAppComponent from "../lib/online_app_component";
import {AuxHeader} from "./aux_header";
import {AuxContent} from "./aux_content";
import {AuxShadow} from "./aux_shadow";
import {auxHeaderContent, auxHeaderLoadingContent} from "../views/aux_header_content";
import { evalScripts } from '../../helpers/scripts';
import DOMUpdateEvent from "../../helpers/dom_update_event";
import verge from 'verge';
import {scrollIntoView} from "scroll-js";

export const OnlineAppAuxConfig = {
    baseHtmlClass: 'online_app__aux',
    selector: '.online_app__aux'
}

export class OnlineAppAux extends OnlineAppComponent {

    constructor(id_or_element, onlineApp) {
        super(id_or_element, onlineApp, OnlineAppAuxConfig);
        if (!this.element) { return };
        this.currentResource = null;
        this.header = new AuxHeader(this);
        this.content = new AuxContent(this);
        this.shadow = new AuxShadow(this);
        this.element.parentElement.insertBefore(this.shadow.element, this.element);

        this.scrollTicking = false;
        this.onlineApp.env.addEventListener('scroll', this.handleScroll.bind(this));

        this.resizeTicking = false;
        this.onlineApp.env.addEventListener('resize', this.handleResize.bind(this));
        this.renderTopOffset(verge.viewportH());
    }

    handleResize(event) {
        this.latestViewportHeight = verge.viewportH();
        if (!this.resizeTicking) {
            requestAnimationFrame((timestamp) => {
                this.resizeTicking = false;
                this.renderTopOffset(this.latestViewportHeight);
            });
        };
        this.resizeTicking = true;
    }

    renderTopOffset(viewportHeight) {
        const headerHeight = this.header.getHeight();
        this.element.style.top = `${ viewportHeight - headerHeight }px`;
    }

    handleScroll(event) {
        const rect = verge.rectangle(this.element, 0); // cushion of 0px;
        this.latestElementTop = Math.ceil(rect.top);
        this.latestViewportHeight = verge.viewportH();
        if (!this.scrollTicking) {
            requestAnimationFrame((timestamp) => {
                this.scrollTicking = false;
                this.renderForScrollPosition(this.latestElementTop, this.latestViewportHeight);
                this.handleMinimizing(this.latestElementTop, this.latestViewportHeight);
            });
        };
        this.scrollTicking = true;
    }

    renderForScrollPosition(elementTop, viewportHeight=null) {
        if (viewportHeight === null) { viewportHeight = verge.viewportH() };

        this.renderFullscreenState(elementTop <= 0);
        this.renderScaled(elementTop, viewportHeight);
        this.renderFadeOutShadow(elementTop, viewportHeight);
    }

    // Return the portion of the trigger area that the Aux element is currently scrolled down (0 is top and 1 is bottom).
    // The trigger area will be 20% of the viewport height.
    getPortionOfScroll(elementTop, triggerViewportHeightPortion=1, viewportHeight=null) {
        if (viewportHeight === null) { viewportHeight = verge.viewportH() };

        // Get the trigger area height as a portion of the viewport's height.
        const triggerHeight = Math.floor(viewportHeight * triggerViewportHeightPortion);

        // Get the portion of the trigger area that the Aux element has been scrolled down.
        let portion = (elementTop / triggerHeight);

        // Constrain the portion to 0..1
        if (portion < 0) {
            portion = 0;
        } else if (portion > 1) {
            portion = 1;
        }

        return portion;
    }

    // As the element is scrolled down from the top of the viewport we adjust it's scale so that is shrinks slightly as it scrolls down and off the bottom of the page.
    // We transition its scale from 1.0 at the top down to the `scale` variable's value (
    renderScaled(elementTop, viewportHeight) {
        const portion = this.getPortionOfScroll(elementTop, 0.5, viewportHeight);

        const maxScaleReduction = 0.1; // Once the Aux element has been scrolled to the bottom of the trigger area it will be scaled down by 10%
        const fullScale = 1;

        const scaleReduction = (maxScaleReduction * portion);
        const scale = fullScale - scaleReduction;
        this.element.style.transform = `scale(${ scale })`;
    }

    renderFadeOutShadow(elementTop, viewportHeight) {
        let portion = this.getPortionOfScroll(elementTop, 1, viewportHeight);
        this.shadow.element.style.height = `${ (1- portion) * 100 }vh`;
        this.shadow.element.style.minHeight = `${ this.header.getHeight() * 2 }px`;
    }

    // If we're not currently "scrollingTo" the element and
    // If the Aux element is scrolled to the bottom of the viewport then we dispatch the auxScrolledOff action.
    // The threshold for where the bottom of the viewport is determined by whether the aux is minimized or not.
    handleMinimizing(elementTop, viewportHeight=null) {
        if (!this.isScrollingTo) {
            if (viewportHeight === null) { viewportHeight = verge.viewportH() };
            // const portionOfViewportHeight = (!!this.readStateMachineValue('auxMinimized')) ? 0.9 : 0.94;
            const headerHeight = this.header.getHeight();
            const scrolledOffThreshold = viewportHeight - headerHeight - 5;
            if (elementTop > scrolledOffThreshold) {
                this.renderMinimizedState(true);
                this.dispatchAction('auxScrolledOff');
            } else {
                this.renderMinimizedState(false);
                this.dispatchAction('auxScrolledOn');
            };
        }
    }

    renderMinimizedState(state) {
        if (state) {
            this.addHtmlClassState('minimized');
            this.shadow.addHtmlClassState('minimized');
        } else {
            this.shadow.removeHtmlClassState('minimized');
            this.removeHtmlClassState('minimized');
        };
    }

    renderFullscreenState(state) {
        if (state) {
            this.addHtmlClassState('fullscreen');
        } else {
            this.removeHtmlClassState('fullscreen');
        };
    }

    listenOnAuxClosers() {
        this.element.querySelectorAll('.online_app__aux__closer').forEach( (element) => {
            element.addEventListener('click', (event) => {
                event.preventDefault();
                this.dispatchAction('auxClosed');
            });
        });
    }

    renderContent(text, url=null, name=null) {
        this.content.setUrl(url);
        this.content.setContent(text);
        const pageHeadingElement = parsePageHeadingElement(text);
        if (pageHeadingElement) {
            this.header.setContent(pageHeadingElement.outerHTML);
        } else {
            this.header.setContent(auxHeaderContent({ name: name }));
        };
        if (this.header.hasContent() ) { this.element.appendChild(this.header.refreshedElement()) };
        if (this.content.hasContent() ) {
            this.element.appendChild(this.content.refreshedElement());
            this.listenOnAuxClosers();
            evaluateInlineScripts(text);
            fireDOMUpdateEvent();
        };
    }

    eraseContent() {
        this.content.setUrl(null);
        this.content.setContent(null);
        this.content.setTitle(null);
        this.header.setContent(null);
        this.element.innerHTML = '';
        fireDOMUpdateEvent();
    }

    renderOpenState(state) {
        if (state) {
            this.shadow.addHtmlClassState('open');
            this.addHtmlClassState('open');
        } else {
            this.shadow.removeHtmlClassState('open');
            this.removeHtmlClassState('open');
        };
    }

    renderLoadingState(state) {
        if (state) {
            this.addHtmlClassState('loading');
        } else {
            this.removeHtmlClassState('loading');
        };
    }

    renderResource(url, name=null) {
        this.header.setContent(auxHeaderLoadingContent({ name: name }));
        if (this.header.hasContent() ) { this.element.appendChild(this.header.refreshedElement()) }

        this.onlineApp.prefetcher.contentFor(url, 'OnlineAppAuxContentFragment', (content) => {
            if (content) {
                this.onlineApp.env.setTimeout(() => {
                    this.renderLoadingState(false);
                    this.renderContent(content, url, name);
                    this.renderTopOffset(verge.viewportH());
                    this.renderOpenState(true);
                    this.scrollTo(() => {
                        this.renderForScrollPosition(0);
                    });
                }, 1); // Adjust this duration to simulate a slow server response.
            };
        });
    }

    isRenderedWith(resource) {
        return this.currentResource === resource;
    }

    // The Aux is loading when there is a resource URL and it has not been rendered with that resource URL yet.
    isLoading() {
        const resourceUrl = this.readStateMachineValue('auxResourceUrl');
        return !!resourceUrl && !this.isRenderedWith(resourceUrl);
    }

    render() {

        const resourceUrl = this.readStateMachineValue('auxResourceUrl');
        const resourceName = this.readStateMachineValue('auxResourceName');

        if (resourceUrl) {
            if (!this.isRenderedWith(resourceUrl)) {
                this.renderLoadingState(true);
                this.renderResource(resourceUrl, resourceName);
            }
        } else {
            this.renderOpenState(false);
            this.eraseContent();
            this.renderLoadingState(false);
        }

        this.currentResource = resourceUrl;

        this.renderMinimizedState(this.readStateMachineValue('auxMinimized'));

    }

    scrollTo(callback=null) {
        this.isScrollingTo = true;
        scrollIntoView(this.element, document.body, { behavior: 'smooth' }).then( () => {
            scrollIntoView(this.element, document.body).then( () => {
                this.isScrollingTo = false;
                if (callback) { callback() }
            } );
        });
    }

}

export function getAux(element, onlineApp) {
    if (!element) return null;
    return new OnlineAppAux(element, onlineApp);
}


function evaluateInlineScripts(text){
    if (evalScripts) {
        evalScripts(text);
    } else {
        console.warn("[OnlineApp] Inline scripts were not eval'd.");
    };
};

function fireDOMUpdateEvent(){
    if (DOMUpdateEvent) {
        new DOMUpdateEvent({ fireOnDOMContentLoaded: false }).fire();
    } else {
        console.warn("[OnlineApp] DOMUpdateEvent was not fired.");
    };
};

function parsePageHeadingElement(text, selector=".page_heading") {
    let tempElement = document.createElement('div');
    tempElement.innerHTML = text;
    let element = tempElement.querySelector(selector);
    if (element) {
        return element;
    } else {
        return null;
    }
}